import { CircularProgress, IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material";
import React, { createElement, useMemo, useState } from "react";
import { TbDotsVertical } from "react-icons/tb";

import useBulkUETDS from "@/providers/hooks/useBulkUETDS";
import { useTrackingActions } from "@/stores/use-tracking-table";
import { IPlanPassenger, Task } from "@/types/tasks.types";

import { TrackingClickActionNames, trackingActions } from "../table/TrackingActionsColumn";

function MobileTrackingActions({ task }: { task: Task<IPlanPassenger<"api">> }) {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const { handleBulkUETDS, isPending } = useBulkUETDS();
    const { updateShowBulkCancelDialog, updateShowBulkUnassignDialog } = useTrackingActions();

    const open = Boolean(anchorEl);

    const clickActions: Partial<Record<TrackingClickActionNames, { cb: () => void; cond: boolean }>> = useMemo(
        () => ({
            [TrackingClickActionNames.UETDS_REGISTER]: {
                cb: async () => handleBulkUETDS([task.id]),
                cond: !task.uetdsId,
            },
            [TrackingClickActionNames.UETDS_CANCEL]: {
                cb: async () => {
                    updateShowBulkCancelDialog({ ids: [task.id] });
                },
                cond: !!task.uetdsId,
            },
            [TrackingClickActionNames.TASK_CANCEL]: {
                cb: async () => {
                    const ids = (task.passengers || []).map((passenger) => passenger.id);
                    if (!ids.length) return;
                    updateShowBulkUnassignDialog({
                        ids,
                        totalPassengers: ids.length,
                    });
                },
                cond: true,
            },
        }),
        [handleBulkUETDS, updateShowBulkCancelDialog, updateShowBulkUnassignDialog, task]
    );

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };
    const mobileActions = useMemo(
        () =>
            trackingActions.filter(
                (action) => action.mobile && clickActions[action.onClick] && clickActions[action.onClick]?.cond
            ),
        [clickActions]
    );

    return (
        <>
            {isPending && (
                <div className=" absolute inset-0 grid place-content-center z-50">
                    <CircularProgress />
                </div>
            )}
            <IconButton
                disabled={isPending}
                onClick={handleClick}
                size="small"
                className=" !w-[30px] grow-0 shrink-0 !h-[30px]"
            >
                <TbDotsVertical size={18} />
            </IconButton>
            <Menu
                sx={{
                    ":root": {},
                }}
                classes={{
                    paper: "!min-w-[178.58px]",
                    list: "!min-w-[178.58px]",
                }}
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                onClick={handleClose}
            >
                {mobileActions.map((action) => (
                    <MenuItem
                        disabled={isPending}
                        key={action.label}
                        onClick={clickActions[action.onClick]?.cb}
                        sx={{}}
                    >
                        <ListItemIcon>{createElement(action.icon, { color: action.color })}</ListItemIcon>
                        <ListItemText>{action.label}</ListItemText>
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}

export default MobileTrackingActions;
